import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload } from 'lucide-react'
import { viewerApi } from '@viewer/lib/viewerApi'
import { canOpenFragments } from '@core/session/modelFileTypes'
import { AuthContext } from '@core/auth/useAuth'
import { useAppStore } from '@core/store'
import { latestParsed } from '@modules/bcf/store/bcfClientSlice'
import { importarBcfNaBiblioteca } from '@modules/bcf/lib/bcfImportar'
import { MENSAGEM_BCF_SEM_MODULO, mensagemBcfImportado, separarLargada } from './largada'

// Drag an IFC (or .ifczip) onto the viewport to load it. Dropped files are
// added to the federation (replace:false), so several can be dropped at
// once. Listens on the window so the whole app is a drop target, and only
// reacts to file drags. preventDefault on the file drop also stops the
// browser's default "navigate to the dropped file" behaviour.
//
// Um .bcfzip ou .bcf largado vai para a biblioteca do módulo BCF, pela
// mesma importação do botão "+ Importar BCF" (um ProjectId que já lá
// está entra como versão nova), com um aviso e o botão "Abrir". Sem o
// módulo (modo local, ou um workspace sem ele) não há onde o guardar, e
// o aviso diz isso. A separação está em largada.ts.
export default function DropZone() {
  const [active, setActive] = useState(false)
  const auth = useContext(AuthContext)
  const navigate = useNavigate()
  // O tratador do drop é instalado uma vez; lê o contexto de agora.
  const contexto = useRef({ auth, navigate })
  useEffect(() => {
    contexto.current = { auth, navigate }
  }, [auth, navigate])

  useEffect(() => {
    let depth = 0
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files')

    const importarBcf = async (f: File) => {
      const { auth: a, navigate: ir } = contexto.current
      const store = useAppStore.getState()
      const workspace = a?.workspace ?? null
      const ligado = a?.mode === 'cloud' && a.modules.includes('bcf') && workspace !== null
      if (!ligado) {
        store.pushToast(MENSAGEM_BCF_SEM_MODULO, { type: 'info', timeout: 9000 })
        return
      }
      const { entry, pairedTo } = await importarBcfNaBiblioteca(f)
      if (!entry) {
        store.pushToast(useAppStore.getState().bcfImportError ?? `Falha a ler ${f.name}.`, { type: 'error' })
        return
      }
      store.pushToast(
        mensagemBcfImportado({
          ficheiro: f.name,
          nome: entry.name,
          topicos: latestParsed(entry).topics.length,
          versaoNova: pairedTo !== null,
        }),
        {
          type: 'success',
          timeout: 9000,
          acao: {
            rotulo: 'Abrir',
            executar: () => {
              useAppStore.getState().bcfSetActive(entry.localId)
              ir(`/${workspace.slug}/bcf/${entry.localId}`)
            },
          },
        },
      )
    }

    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return
      depth += 1
      setActive(true)
    }
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault() // required for the drop event to fire
    }
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) setActive(false)
    }
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      depth = 0
      setActive(false)
      const { modelos, bcf } = separarLargada(Array.from(e.dataTransfer?.files ?? []))
      if (modelos.length === 0 && bcf.length === 0) return
      void (async () => {
        for (const f of modelos) {
          try {
            await viewerApi.loadIfcFile(f, { replace: false })
          } catch (err) {
            window.alert(
              err instanceof Error ? err.message : `Falha a abrir ${f.name}.`,
            )
          }
        }
        for (const f of bcf) {
          try {
            await importarBcf(f)
          } catch (err) {
            useAppStore
              .getState()
              .pushToast(err instanceof Error ? err.message : `Falha a ler ${f.name}.`, { type: 'error' })
          }
        }
      })()
    }

    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragover', onOver)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  if (!active) return null
  return (
    <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-[var(--color-bg)]/70 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[var(--color-accent)] px-8 py-6 text-[var(--color-accent)]">
        <Upload size={32} />
        <span className="text-sm font-medium">Largar o IFC ou o BCF para abrir</span>
        <span className="text-xs text-[var(--color-text-muted)]">
          {canOpenFragments()
            ? '.ifc, .ifczip ou .frag (adiciona à federação)'
            : '.ifc ou .ifczip (adiciona à federação)'}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          .bcfzip ou .bcf (vai para o módulo BCF)
        </span>
      </div>
    </div>
  )
}
